import { ReactNode, useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Library, Network, Folder, Layers } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { VscDebugDisconnect } from 'react-icons/vsc'
import { ChatWidget } from './ChatWidget'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)
  const profileDropdownRef = useRef<HTMLDivElement>(null)

  const navigation = [
    { name: 'Library', href: '/library', icon: Library },
    { name: 'Knowledge Hub', href: '/learning-paths', icon: Layers },
    { name: 'Spaces', href: '/spaces', icon: Folder },
    { name: 'Knowledge Graph', href: '/knowledge-graph', icon: Network },
  ]

  const bottomNavigation = [
    { name: 'Connect', href: '/integrations', icon: VscDebugDisconnect },
  ]

  // Welcome dialog disabled - using modern inline greeting instead
  // useEffect(() => {
  //   const hasSeenWelcome = sessionStorage.getItem('hasSeenWelcome');
  //   const urlParams = new URLSearchParams(location.search);
  //   const hasContentParam = urlParams.has('content') || urlParams.has('highlight');
  //   
  //   // Don't show welcome if navigating to specific content
  //   if (user && !hasSeenWelcome && location.pathname === '/library' && !hasContentParam) {
  //     // Wait a moment for the page to load
  //     const timer = setTimeout(() => {
  //       setShowWelcome(true);
  //       sessionStorage.setItem('hasSeenWelcome', 'true');
  //     }, 1000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [user, location.pathname, location.search]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isClickInsideButton = profileMenuRef.current?.contains(target)
      const isClickInsideMenu = profileDropdownRef.current?.contains(target)
      
      if (!isClickInsideButton && !isClickInsideMenu) {
        setShowProfileMenu(false)
      }
    }

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showProfileMenu])

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex">
      {/* Left Sidebar - Always Visible */}
      <aside className="bg-white dark:bg-gray-800 flex flex-col fixed h-full w-20 border-r border-gray-200 dark:border-gray-700">
        {/* Logo */}
        <div className="px-2 pt-4 pb-3">
          <Link to="/library" className="flex flex-col items-center gap-1">
            <div className="w-16 h-16 flex items-center justify-center">
              <img 
                src="/iconextn.png" 
                alt="Memtrail Logo" 
                className="w-10 h-10 object-contain"
              />
            </div>
          </Link>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg transition-all text-xs font-medium ${
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-800 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent hover:scale-105'
                }`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-center leading-tight">{item.name}</span>
              </Link>
            )
          })}
        </nav>

            {/* Bottom Navigation */}
            <div className="px-2 pb-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              {bottomNavigation.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`w-full flex flex-col items-center justify-center gap-1 px-1 py-2 rounded-lg transition-all text-xs font-medium ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-2 border-blue-200 dark:border-blue-800 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent hover:scale-105'
                    }`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-center leading-tight break-words w-full">{item.name}</span>
                  </Link>
                )
              })}
            </div>
        
        {/* User Profile Menu */}
        <div className="p-3 relative" ref={profileMenuRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="w-full flex items-center justify-center px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
              </span>
            </div>
          </button>

          {/* Profile Dropdown Menu - Rendered via portal outside sidebar */}
          {showProfileMenu && createPortal(
            <div ref={profileDropdownRef} className="fixed left-20 bottom-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-[99999] w-[200px]">
              {/* User Info Header */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-teal-600 dark:text-teal-400">
                      {(user?.name || user?.fullName || user?.email || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.name || user?.fullName || user?.email?.split('@')[0]?.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Settings */}
              <div className="py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowProfileMenu(false)
                    navigate('/settings')
                  }}
                  className="w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                >
                  Settings
                </button>
              </div>
              
              {/* Logout */}
              <div className="border-t border-gray-100 dark:border-gray-700 py-1">
                <button
                  onClick={async (e) => {
                    e.stopPropagation()
                    setShowProfileMenu(false)
                    await logout()
                    // Small delay to ensure auth state is fully cleared before navigation
                    setTimeout(() => navigate('/login', { replace: true }), 50)
                  }}
                  className="w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left"
                >
                  Logout
                </button>
              </div>
            </div>,
            document.body
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 bg-gray-50 dark:bg-gray-900 ml-20">
        {children}
      </main>

      {/* Floating Chat Widget - Available on all pages */}
      <ChatWidget />

      {/* Welcome Dialog - Disabled, using modern inline greeting instead */}
    </div>
  )
}